import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { bookingService } from '../../services/bookingService';
import Header from '../../components/layout/Header';
import BookingFlowNavigation from '../../components/navigation/BookingFlowNavigation';
import ConfirmationHeader from './components/ConfirmationHeader';
import BookingDetailsCard from './components/BookingDetailsCard';
import PricingBreakdown from './components/PricingBreakdown';
import PaymentStatusCard from './components/PaymentStatusCard';
import ContactInformation from './components/ContactInformation';
import NextStepsCard from './components/NextStepsCard';
import ActionButtons from './components/ActionButtons';
import Icon from '../../components/AppIcon';
import BackButton from '../../components/BackButton';
import { formatNumber } from '../../utils/formatNumber';
import { getPhotographyTypeLabelAr, getShootingMethodLabelAr, getWorkloadLabelAr, getShootingPlaceTypeLabelAr, getProductPhysicalSizeLabelAr } from '../../utils/bookingLabels';

const BookingConfirmation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const bookingId = searchParams.get('id');
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (bookingId) {
      loadBooking();
    } else {
      setError('رقم الحجز غير موجود');
      setLoading(false);
    }
  }, [bookingId]);

  const loadBooking = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await bookingService.getBookingById(bookingId);
      
      if (!data) {
        setError('الحجز غير موجود');
        return;
      }
      
      setBooking(data);
      console.log('✅ Booking loaded for confirmation:', data);
    } catch (err) {
      console.error('❌ Failed to load booking:', err);
      setError('فشل تحميل بيانات الحجز. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  // Transform booking data to match component expectations
  const transformBookingData = (booking) => {
    if (!booking) return null;

    // Format date
    const formatDate = (dateStr) => {
      if (!dateStr) return 'غير محدد';
      const date = new Date(dateStr);
      const days = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
      const months = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
      return `${days[date.getDay()]}، ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
    };

    // Format time
    const formatTime = (timeStr) => {
      if (!timeStr) return 'غير محدد';
      const [hours, minutes] = timeStr.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'مساءً' : 'صباحاً';
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      return `${displayHour}:${minutes || '00'} ${ampm}`;
    };

    // Get pricing snapshot
    const pricingSnapshot = booking.pricingSnapshot || booking.pricing_snapshot || {};
    const basePrice = pricingSnapshot.baseHourPrice || pricingSnapshot.basePrice || 0;
    // finalPriceSar is the camelCase of final_price_sar
    const totalPrice = parseFloat(booking.finalPriceSar || booking.price || 0);
    const subtotal = pricingSnapshot.subtotal || (totalPrice / 1.15);
    const vat = pricingSnapshot.vatAmount || (totalPrice - subtotal);
    const equipmentFee = pricingSnapshot.equipmentFee || 0;
    const serviceFee = pricingSnapshot.serviceFee || 0;
    const discount = pricingSnapshot.discount || 0;
    // Raw breakdown array from DB for accurate itemized display
    const breakdownItems = pricingSnapshot.breakdown || [];

    // Get photographer profile (use photographer_id or assigned_photographer_id)
    const photographerId = booking.photographerId || booking.photographer_id || booking.assignedPhotographerId || booking.assigned_photographer_id;
    const photographer = booking.photographer || {};
    const photographerProfile = booking.photographerProfile || {};
    
    // Get experience level text
    const experienceLevelMap = {
      'certified': 'معتمد',
      'distinguished': 'مميز',
      'elite': 'نخبة',
      // Legacy
      'beginner': 'معتمد',
      'intermediate': 'مميز',
      'advanced': 'مميز',
      'expert': 'نخبة'
    };
    const experienceLevel = experienceLevelMap[photographerProfile.experienceLevel || photographerProfile.experience_level] || 'معتمد';

    // Get equipment list
    const equipment = Array.isArray(booking.equipment) 
      ? booking.equipment 
      : Array.isArray(pricingSnapshot.equipment)
        ? pricingSnapshot.equipment
        : [];

    // Build location text and details
    let locationText = booking.location || 'غير محدد';
    let locationDetails = null;
    if (booking.locationDetails) {
      try {
        locationDetails = typeof booking.locationDetails === 'string' 
          ? JSON.parse(booking.locationDetails) 
          : booking.locationDetails;
        if (locationDetails.address_text) {
          locationText = locationDetails.address_text;
        }
      } catch (e) {
        // Keep default locationText
      }
    }

    // Build session requirements from pricing snapshot
    // shootingMethod lives in multipliers.shootingMethod.key (not at root)
    const shootingMethodKey =
      pricingSnapshot.multipliers?.shootingMethod?.key ||
      pricingSnapshot.shootingMethod ||
      booking.shootingMethod;
    const shootingMethods = pricingSnapshot.shootingMethods ||
      (shootingMethodKey ? [shootingMethodKey] : []);

    // experienceLevel lives in multipliers.experience.key (not at pricingSnapshot.experienceLevel)
    const reqExperienceLevel = (() => {
      const lvl =
        pricingSnapshot.multipliers?.experience?.key ||
        pricingSnapshot.experienceLevel;
      if (lvl) {
        const map = { certified: 'معتمد', beginner: 'معتمد', intermediate: 'مميز', distinguished: 'مميز', advanced: 'مميز', elite: 'نخبة', expert: 'نخبة' };
        return map[lvl] || pricingSnapshot.experienceLevelLabel || lvl;
      }
      return pricingSnapshot.experienceLevelLabel || null;
    })();

    return {
      bookingReference: `BK-${booking.id?.substring(0, 8).toUpperCase()}`,
      bookingDetails: {
        photographerName: photographer.full_name || photographer.fullName || photographerProfile.full_name || 'مصور',
        photographerImage: photographer.avatar_url || photographer.avatarUrl || photographerProfile.avatar_url || 'https://via.placeholder.com/150',
        photographerImageAlt: `صورة ${photographer.full_name || photographer.fullName || 'المصور'}`,
        photographerRating: photographerProfile.rating?.toFixed(1) || '4.5',
        photographerReviews: photographerProfile.total_reviews || photographerProfile.totalReviews || '0',
        photographerCity: booking.city || photographerProfile.city || 'الرياض',
        date: formatDate(booking.bookingDate || booking.scheduledAt || booking.created_at),
        time: formatTime(booking.bookingTime),
        location: locationText,
        locationDetails: locationDetails,
        duration: booking.duration || `${pricingSnapshot.hours || 2} ساعة`,
        photographyType: getPhotographyTypeLabelAr(booking.photographyType || booking.photography_type),
        experienceLevel: `مصور ${experienceLevel}`,
        equipment: equipment,
        sessionRequirements: {
          photographyType: booking.photographyType || booking.photography_type || null,
          shootingMethods,
          workloadBucket: pricingSnapshot.workloadBucket || booking.workloadBucket || null,
          shootingPlaceType: pricingSnapshot.shootingPlaceType || booking.shootingPlaceType || booking.shooting_place_type || null,
          productPhysicalSizeBucket: pricingSnapshot.productPhysicalSizeBucket || pricingSnapshot.product_physical_size_bucket || null,
          requestedExperienceLevel: reqExperienceLevel,
          photosCount: pricingSnapshot.photosCount ?? pricingSnapshot.photos_count ?? null,
          videosCount: pricingSnapshot.videosCount ?? pricingSnapshot.videos_count ?? null,
          shortVideosCount: pricingSnapshot.shortVideosCount ?? pricingSnapshot.short_videos_count ?? null,
          deliverableLabel: pricingSnapshot.deliverableLabel || pricingSnapshot.deliverable_label || null,
          deliverableMode: pricingSnapshot.deliverableMode || null,
          notes: booking.notes || null,
        }
      },
      pricing: {
        basePrice: basePrice,
        equipmentFee: equipmentFee,
        serviceFee: serviceFee,
        discount: discount,
        subtotal: subtotal,
        tax: vat,
        total: totalPrice,
        breakdown: breakdownItems,
      },
      paymentStatus: {
        status: booking.status === 'paid_confirmed' ? 'paid' : booking.status === 'awaiting_payment' ? 'pending' : 'pay_later',
        method: booking.paymentTransactionId || booking.payment_transaction_id ? 'تم الدفع' : 'الدفع عند استلام الخدمة',
        dueDate: booking.paymentExpiresAt ? formatDate(booking.paymentExpiresAt) : null
      },
      customer: {
        name: booking.customer?.full_name || booking.customer?.fullName || 'عميل',
        email: booking.customer?.email || '',
        phone: booking.customer?.phone || ''
      },
      photographer: {
        name: photographer.full_name || photographer.fullName || photographerProfile.full_name || 'مصور',
        email: photographer.email || photographerProfile.email || '',
        phone: photographer.phone || photographerProfile.phone || ''
      },
      nextSteps: booking.status === 'paid_confirmed' ? [
        {
          title: 'الحجز مؤكد ومدفوع',
          description: 'أُرسلت تفاصيل الحجز إلى بريدك الإلكتروني',
          timeline: 'الآن'
        },
        {
          title: 'تواصل مع المصور',
          description: 'اتصل بالمصور لمناقشة أي تفاصيل إضافية قبل الموعد',
          timeline: 'قبل الموعد'
        },
        {
          title: 'استلام الصور',
          description: 'تُسلَّم الصور المعدّلة خلال 7–10 أيام عمل',
          timeline: 'بعد الجلسة'
        }
      ] : [
        {
          title: 'انتظار موافقة المصور',
          description: 'يرد المصور خلال 24 ساعة وستصلك رسالة تأكيد',
          timeline: 'خلال 24 ساعة'
        },
        {
          title: 'حضور الجلسة',
          description: 'احضر في الموعد المحدد، يُفضَّل الوصول قبل 15 دقيقة',
          timeline: formatDate(booking.bookingDate || booking.scheduledAt)
        },
        {
          title: 'استلام الصور',
          description: 'تُسلَّم الصور المعدّلة خلال 7–10 أيام عمل',
          timeline: 'بعد الجلسة'
        }
      ]
    };
  };

  const bookingData = transformBookingData(booking);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-slate-600">جاري تحميل بيانات الحجز...</p>
        </div>
      </div>
    );
  }

  if (error || !booking || !bookingData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" dir="rtl">
        <div className="text-center max-w-md mx-auto px-4">
          <Icon name="AlertCircle" size={64} className="mx-auto mb-4 text-error" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">خطأ في تحميل الحجز</h2>
          <p className="text-slate-600 mb-6">{error || 'الحجز غير موجود'}</p>
          <button
            onClick={() => navigate('/customer-dashboard/bookings')}
            className="px-6 py-3 bg-button text-button-foreground rounded-lg hover:opacity-90 transition-colors"
          >
            العودة إلى الحجوزات
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 pb-20 lg:pb-8 px-4 md:px-6 lg:px-8">
        <div className="max-w-lg mx-auto" dir="rtl">
          <div className="mb-4">
            <BackButton to="/customer-dashboard/bookings" label="العودة إلى الحجوزات" />
          </div>

          <ConfirmationHeader bookingReference={bookingData?.bookingReference} />

          <div className="space-y-3 mt-4">
            <BookingDetailsCard
              bookingDetails={bookingData?.bookingDetails}
              totalPrice={bookingData?.pricing?.total}
              paymentStatus={bookingData?.paymentStatus}
            />
            <ContactInformation photographer={bookingData?.photographer} />
            <NextStepsCard steps={bookingData?.nextSteps} />
          </div>

          <div className="mt-4">
            <ActionButtons bookingReference={bookingData?.bookingReference} />
          </div>
        </div>
      </main>
    </div>);

};

export default BookingConfirmation;